// © 2013 - 2024 Tinder, Inc., ALL RIGHTS RESERVED

import { cx } from '@linaria/core';
import React, { forwardRef, memo, useCallback, useReducer, isValidElement } from 'react';
import {
  SIZING_ICON_SMALL,
  SIZING_ICON_MEDIUM,
  SIZING_ICON_LARGE,
  SIZING_BUTTON_LARGE_ICON,
  SIZING_BUTTON_MEDIUM_ICON,
  SIZING_BUTTON_SMALL_ICON,
} from '@tinderapp/ui-core';

import { useKeyboardFocus } from '../../utils/hooks';

import { ButtonProps } from './types';
import LoadingSpinner from '../LoadingSpinner';

import styles from './styles';

const initialState = {
  hovered: false,
  tapped: false,
};

type ReducerState = typeof initialState;

function reducer(state: ReducerState, action: Partial<ReducerState>) {
  return { ...state, ...action };
}

const loaderSize = {
  small: SIZING_ICON_SMALL,
  medium: SIZING_ICON_MEDIUM,
  large: SIZING_ICON_LARGE,
};

const iconSize = {
  small: SIZING_BUTTON_SMALL_ICON,
  medium: SIZING_BUTTON_MEDIUM_ICON,
  large: SIZING_BUTTON_LARGE_ICON,
};

const Button: React.ForwardRefRenderFunction<HTMLAnchorElement | HTMLButtonElement, ButtonProps> = (
  props,
  ref
) => {
  const {
    // react
    children,
    className,
    type,
    // config
    size,
    width,
    height,
    dir,
    // colors
    background,
    border,
    foreground,
    overlay,
    focus,
    // icon
    leadingIcon,
    trailingIcon,
    // state
    disabled,
    loading,
    // ui state
    overrideFocused,
    overrideHovered,
    overrideTapped,
    // handlers
    onFocus,
    onBlur,
    onMouseEnter,
    onMouseLeave,
    onPointerDown,
    onPointerUp,
    onPointerCancel,
    ...restProps
  } = props;

  const [isFocused, , setIsFocused] = useKeyboardFocus(false);
  const [state, dispatch] = useReducer(reducer, initialState);

  const handleWindowPointerUp = useCallback(() => {
    dispatch({ tapped: false });
    window.removeEventListener('pointerup', handleWindowPointerUp);
  }, []);

  const handleFocus = useCallback<React.FocusEventHandler>(
    (e) => {
      onFocus && (onFocus as React.FocusEventHandler)(e);
      if (!e.isDefaultPrevented()) {
        setIsFocused(true);
      }
    },
    [onFocus, setIsFocused]
  );

  const handleBlur = useCallback<React.FocusEventHandler>(
    (e) => {
      onBlur && (onBlur as React.FocusEventHandler)(e);
      if (!e.isDefaultPrevented()) {
        setIsFocused(false);
      }
    },
    [onBlur, setIsFocused]
  );

  const handleMouseEnter = useCallback<React.MouseEventHandler>(
    (e) => {
      onMouseEnter && (onMouseEnter as React.MouseEventHandler)(e);
      if (!e.isDefaultPrevented()) {
        dispatch({ hovered: true });
      }
    },
    [onMouseEnter]
  );

  const handleMouseLeave = useCallback<React.MouseEventHandler>(
    (e) => {
      onMouseLeave && (onMouseLeave as React.MouseEventHandler)(e);
      if (!e.isDefaultPrevented()) {
        dispatch({ hovered: false });
      }
    },
    [onMouseLeave]
  );

  const handlePointerDown = useCallback<React.PointerEventHandler>(
    (e) => {
      onPointerDown && (onPointerDown as React.PointerEventHandler)(e);
      const isCtrlLeftButton = e.ctrlKey && e.button === 1;
      const isRightButton = e.button === 2;
      if (!e.isDefaultPrevented() && !(isCtrlLeftButton || isRightButton)) {
        dispatch({ tapped: true });
        window.addEventListener('pointerup', handleWindowPointerUp);
      }
    },
    [onPointerDown, handleWindowPointerUp]
  );

  const handlePointerUp = useCallback<React.PointerEventHandler>(
    (e) => {
      onPointerUp && (onPointerUp as React.PointerEventHandler)(e);
      if (!e.isDefaultPrevented()) {
        dispatch({ tapped: false });
      }
    },
    [onPointerUp]
  );

  const handlePointerCancel = useCallback<React.PointerEventHandler>(
    (e) => {
      onPointerCancel && (onPointerCancel as React.PointerEventHandler)(e);
      if (!e.isDefaultPrevented()) {
        dispatch({ tapped: false });
      }
    },
    [onPointerCancel]
  );

  const cssVariables = {
    [`--tui-button-background`]: background,
    [`--tui-button-border`]: border,
    [`--tui-button-foreground`]: foreground,
    [`--tui-button-overlay`]: overlay,
    [`--tui-button-focus`]: focus,
  } as React.CSSProperties;

  const style = cssVariables;

  // If given in absolute units, clamp to 100% and set use as minimum width.
  // Otherwise, set width directly.
  if (typeof width === 'string' && width.endsWith('%')) {
    style.width = width;
  } else if (width != null) {
    style.minWidth = `min(100%, ${width})`;
  }

  const buttonProps = {
    className: cx(styles.container, className),
    style: style,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onPointerDown: handlePointerDown,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
    'data-size': size,
    'data-disabled': disabled ? '' : undefined,
    'data-dir': dir,
    'data-loading': loading ? '' : undefined,
    'data-focused': overrideFocused ?? isFocused ? '' : undefined,
    'data-hovered': !disabled && (overrideHovered ?? state.hovered) ? '' : undefined,
    'data-tapped': !disabled && (overrideTapped ?? state.tapped) ? '' : undefined,
    'data-icon-leading': leadingIcon ? '' : undefined,
    'data-icon-trailing': trailingIcon ? '' : undefined,
  };

  const buttonContent = (
    <>
      <div className={styles.touchTarget} />
      <div className={styles.wrapper}>
        <span className={styles.focus} />
        <div className={styles.fill} />
        {loading && (
          <div className={styles.loader}>
            <LoadingSpinner color={foreground} size={loaderSize[size]} stroke={1.5} />
          </div>
        )}
        <div className={styles.content} style={{ minHeight: height }}>
          {leadingIcon && (
            <div className={styles.iconLeading}>
              {isValidElement(leadingIcon)
                ? leadingIcon
                : React.createElement(leadingIcon, {
                    fill: foreground,
                    height: iconSize[size],
                    width: iconSize[size],
                  })}
            </div>
          )}
          <div className={styles.label}>{children}</div>
          {trailingIcon && (
            <div className={styles.iconTrailing}>
              {isValidElement(trailingIcon)
                ? trailingIcon
                : React.createElement(trailingIcon, {
                    fill: foreground,
                    height: iconSize[size],
                    width: iconSize[size],
                  })}
            </div>
          )}
        </div>
        <span className={styles.overlay} />
      </div>
    </>
  );

  if (props.href) {
    return (
      <a
        {...(restProps as React.ComponentProps<'a'>)}
        {...buttonProps}
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
      >
        {buttonContent}
      </a>
    );
  }

  return (
    <button
      {...(restProps as React.ComponentProps<'button'>)}
      {...buttonProps}
      type={type || 'button'}
      disabled={disabled}
      ref={ref as React.ForwardedRef<HTMLButtonElement>}
    >
      {buttonContent}
    </button>
  );
};

export default memo(forwardRef(Button));
